/**
 * Prelievo multi-riga — parsing dei due 409 che guidano il flusso (#116).
 *
 * Distinti da `lib/saldo.ts`, che copre il 422 della regola assoluta: quello
 * vale su sei percorsi diversi (rettifiche, eliminazioni, riallocazione), questi
 * due appartengono al solo prelievo e servono a portare l'operatore alla
 * schermata giusta. Tenerli separati evita il file-contenitore in cui, fra sei
 * mesi, nessuno sa più quale parser appartiene a quale regola.
 *
 *  - `scarico_parziale`: una riga eccede il suo materiale. Il payload porta i
 *    DISPONIBILI dell'area, non una ripartizione: la scelta è dell'operatore.
 *  - `conferma_riduzione_richiesta`: l'area non copre il peso chiesto e lo
 *    scarto supera la soglia. Serve un sì esplicito.
 */

export interface DisponibileMateriale {
  materiale: string;
  disponibile_kg: number;
}

export interface ScaricoParzialeDetail {
  code: 'scarico_parziale';
  area_id: string;
  area_codice: string | null;
  materiale_insufficiente: string;
  richiesto_kg: number;
  disponibile_kg: number;
  /** Tutti i materiali con giacenza in area, dal più capiente. */
  disponibili: DisponibileMateriale[];
}

export interface ConfermaRiduzioneDetail {
  code: 'conferma_riduzione_richiesta';
  area_codice: string | null;
  peso_richiesto_kg: number;
  peso_registrabile_kg: number;
  scarto_kg: number;
  scarto_pct: number;
  soglia_pct: number;
}

function detail409(err: unknown): Record<string, unknown> | null {
  const response = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  if (response?.status !== 409) return null;
  const detail = response.data?.detail;
  if (!detail || typeof detail !== 'object') return null;
  return detail as Record<string, unknown>;
}

function numero(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

export function parseScaricoParziale(err: unknown): ScaricoParzialeDetail | null {
  const d = detail409(err);
  if (d?.code !== 'scarico_parziale') return null;
  const grezzi = Array.isArray(d.disponibili) ? d.disponibili : [];
  return {
    code: 'scarico_parziale',
    area_id: String(d.area_id ?? ''),
    area_codice: (d.area_codice as string) ?? null,
    materiale_insufficiente: String(d.materiale_insufficiente ?? ''),
    richiesto_kg: numero(d.richiesto_kg),
    disponibile_kg: numero(d.disponibile_kg),
    disponibili: grezzi.map((r) => {
      const x = (r ?? {}) as Partial<DisponibileMateriale>;
      return { materiale: String(x.materiale ?? ''), disponibile_kg: numero(x.disponibile_kg) };
    }),
  };
}

/** 409 di uno scarico gia' trascritto sul registro C/S: non e' un vicolo cieco,
 *  il flag e' reversibile — la strada la mostra la modale, che possiede le
 *  etichette dei bottoni (il backend non deve conoscerle). */
export interface ScaricoRegistratoDetail {
  code: 'scarico_registrato_ecomatico';
  registrato_at: string | null;
}

export function parseScaricoRegistrato(err: unknown): ScaricoRegistratoDetail | null {
  const d = detail409(err);
  if (d?.code !== 'scarico_registrato_ecomatico') return null;
  return {
    code: 'scarico_registrato_ecomatico',
    registrato_at: typeof d.registrato_at === 'string' ? d.registrato_at : null,
  };
}

export function parseConfermaRiduzione(err: unknown): ConfermaRiduzioneDetail | null {
  const d = detail409(err);
  if (d?.code !== 'conferma_riduzione_richiesta') return null;
  return {
    code: 'conferma_riduzione_richiesta',
    area_codice: (d.area_codice as string) ?? null,
    peso_richiesto_kg: numero(d.peso_richiesto_kg),
    peso_registrabile_kg: numero(d.peso_registrabile_kg),
    scarto_kg: numero(d.scarto_kg),
    scarto_pct: numero(d.scarto_pct),
    soglia_pct: numero(d.soglia_pct),
  };
}
